import { useEffect, useState } from "react";
import { useAppContext } from "../../config/AppContext";
import { useAuth } from "../../auth/FirebaseAuth";
import { useSnackbar } from "notistack";
import { getOrgDataById, getUserDataById } from "../../models/DataFunctions";
import { Org, User } from "../../types/basicTypes";
import SecondaryButton from "../../buttons/SecondaryButton";
import { EasySwitch } from "../../textfields/EasySwitch";
import PrimaryButton2 from "../../buttons/PrimaryButton2";
import PrimaryButtonSexy from "../../buttons/PrimaryButtonSexy";
import PrimaryButton from "../../buttons/PrimaryButton";
import PrimaryButtonWhite from "../../buttons/PrimaryButtonWhite";
import stripeCheckoutSession from "../../api/stripe/stripeCheckoutSession";
import { useLocation } from "react-router-dom";
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken";
import stripePortalSession from "../../api/stripe/stripePortalSession";

type OrganisationProps = {
  userData: User;
  orgData: Org;
  navigateWorkspace: (path: string) => void;
  handleClickVariant: (
    message: string,
    variant: "default" | "error" | "success" | "warning" | "info" | undefined
  ) => void;
};

const Facturation = ({
  userData,
  orgData,
  navigateWorkspace,
  handleClickVariant,
}: OrganisationProps) => {
  function getPrice(plan: string, engagement: string, test_env = false) {
    if (plan === "free_tier") {
      return "0€";
    } else if (plan === "business" && engagement === "month") {
      return test_env ? "1€" : "90€";
    } else if (plan === "business" && engagement === "year") {
      return test_env ? "1€" : "75€";
    } else if (plan === "premium" && engagement === "month") {
      return test_env ? "2€" : "180€";
    } else if (plan === "premium" && engagement === "year") {
      return test_env ? "2€" : "150€";
    } else if (plan === "starter" && engagement === "month") {
      return test_env ? "2€" : "35€";
    } else if (plan === "starter" && engagement === "year") {
      return test_env ? "2€" : "29€";
    }
  }

  const { currentUser, loading } = useAuth();

  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const [questions, setQuestions] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  const handleFetchData = async () => {
    if (currentUser) {
      try {
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const location = useLocation();

  const handleOpenStripePortal = async () => {
    const token = await getOrRefreshFirebaseToken();
    let res = await stripePortalSession(orgData.id, token, prefix);
    if (res && res.portal_url) {
      let url = res.portal_url;
      if (url) {
        // open the URL in a new tab
        window.open(url, "_blank");
      }
      handleClickVariant(
        `Redirection vers Stripe pour la gestion de votre abonnement...`,
        "info"
      );
    } else {
      handleClickVariant(
        `Erreur lors de la création de la session de paiement : ${res.detail}`,
        "error"
      );
    }
  };

  const handleStartCheckoutSession = async (plan_type: string) => {
    let url = "https://ath.hexagone.expert";

    let success_url = `${url}/facturation`;
    let cancel_url = `${url}/facturation`;
    const token = await getOrRefreshFirebaseToken();

    let res: any = await stripeCheckoutSession(
      plan_type,
      success_url,
      cancel_url,
      userData.email,
      token,
      prefix
    );
    if (res && res.session_url) {
      let url = res.session_url;
      if (url) {
        // open the URL in a new tab

        window.open(url, "_blank");
      }
      handleClickVariant(
        `Redirection vers Stripe pour le paiement du plan ${plan_type}...`,
        "info"
      );
    } else {
      handleClickVariant(
        `Erreur lors de la création de la session de paiement : ${res.error}`,
        "error"
      );
    }
  };

  useEffect(() => {
    if (currentUser) {
      handleFetchData();
    }
  }, [currentUser]);

  if (loading || !currentUser || !userData) {
    return <> Loading ..</>;
  }

  return (
    <div className="relative   flex flex-col    w-full rounded-lg items-center justify-start">
      <div className="z-10 overflow-auto pb-10 flex flex-col h-fit   w-full bg-gradient-to-t from-transparent to-wshadow_50 rounded-lg p-4">
        <div className="text-h1  font-semibold mb-4">Facturation</div>
        {orgData.facturation?.plan === "free" && (
          <div
            style={{
              width: orgData.counters?.attestations >= 5 ? "450px" : "220px",
            }}
            className={`
            ${orgData.counters?.attestations >= 5 ? "border-red-400" : ""}
            flex flex-col items-start  rounded-3xl bg-black px-5 py-5 gap-2 text-wshadow_25 text-h3 font-medium`}
          >
            <div className="bg-wshadow_50 h-1 mt-2 rounded-full relative w-40">
              <div
                style={{
                  width: `${
                    Math.min(orgData.counters?.attestations || 0, 5) * 20
                  }%`, // Assuming max is 10 attestations, adjust as needed
                }}
                className={`
                    ${
                      orgData.counters?.attestations >= 5
                        ? "bg-red-500"
                        : "bg-white"
                    }
                    absolute top-0 left-0 h-full  rounded-full transition-all duration-300 ease-in-out`}
              ></div>
            </div>
            <div className="text-hx2 ">
              <span className="text-h3 text-wshadow_75 ">
                {orgData.counters?.attestations || 0} / 5 attestations gratuites
              </span>
            </div>
            {orgData.counters?.attestations >= 5 && (
              <div className="text-white text-h3">
                Vous avez atteint la limite de 5 attestations gratuites.{" "}
                <span className="text-h3 text-wshadow_75">
                  Passez à un plan payant pour continuer à générer des
                  attestations.
                </span>
              </div>
            )}
          </div>
        )}
        <div className="flex flex-row items-start gap-4 w-full max-w-c1k mt-4 ">
          <div className="bg-white border rounded-3xl flex flex-col h-fit w-c220 p-4 ">
            <div className="text-hx2 font-medium ">
              {orgData.counters.attestations}
            </div>
            <div className="text-hx2 ">
              <span className="text-h2 text-shadow_50 ">
                Attestations générées
              </span>
            </div>
          </div>{" "}
          <div className="bg-white border rounded-3xl flex flex-col h-fit w-c220 p-4 ">
            <div className="text-hx2 font-medium ">
              {orgData.counters.xmls ?? 0}
            </div>
            <div className="text-hx2 ">
              <span className="text-h2 text-shadow_50 ">Xml déposés</span>
            </div>
          </div>{" "}
          <div className="bg-white border rounded-3xl flex flex-col h-fit w-c220 p-4 ">
            <div className="text-hx2 font-medium ">
              {(orgData.counters.storage ?? 0) < 100000000
                ? ((orgData.counters.storage ?? 0) / 1000000).toFixed(2) + " Mo"
                : ((orgData.counters.storage ?? 0) / 1000000000).toFixed(2) +
                  " Go"}{" "}
              <span className="text-h2 text-shadow_50"> /1 Go</span>
            </div>
            <div className="text-hx2 ">
              <span className="text-h2 text-shadow_50 ">Stockage</span>
            </div>
          </div>
        </div>
        {orgData.facturation?.plan !== "active" && (
          <div className="flex flex-wrap items-start gap-4 w-full max-w-c1k mt-4 ">
            <div className="bg-white border-1  rounded-3xl flex flex-col h-fit w-c220 p-6 shadow-lg">
              <div className="text-hx2 font-bold">Plan 1 mois</div>
              <div className="text-hx2 ">
                45€
                <span className="text-h2 text-shadow_50"> /mois HT</span>
              </div>
              <div className="flex flex-row items-center justify-end gap-2 mt-4">
                <PrimaryButton
                  onClick={() => {
                    handleStartCheckoutSession("monthly");
                  }}
                >
                  Choisir
                </PrimaryButton>
              </div>
            </div>{" "}
            <div className="bg-white border-1  rounded-3xl flex flex-col h-fit p-6  w-c300 shadow-lg">
              <div className="relative flex -top-3 items-center justify-center rounded-full px-2  w-full"></div>
              <div className="text-hx2 font-bold">Plan 1 an</div>
              <div className="text-hx2 ">
                36€
                <span className="text-h2 text-shadow_50"> /mois HT</span>
              </div>

              <div className="flex flex-row items-end justify-between gap-2 mt-4">
                <div className="border-green-400 text-h2 border-2 px-2 py-1 text-black rounded-full w-fit">
                  -20% offerts
                </div>
                <PrimaryButton
                  onClick={() => {
                    handleStartCheckoutSession("annual");
                  }}
                >
                  Choisir
                </PrimaryButton>
              </div>
            </div>
            <div className="bg-black border-2 relative border-black text-white rounded-3xl flex flex-col h-fit  w-c400 shadow-xl">
              <div className="flex flex-col p-6 z-10 ">
                <div className="relative flex -top-3 items-center justify-center rounded-full px-2  w-full"></div>
                <div className="text-hx2 font-bold">Plan 3 ans</div>
                <div className="text-hx2 ">
                  23€
                  <span className="text-h2 text-wshadow_80"> /mois HT</span>
                </div>

                <div className="flex flex-row items-end justify-between gap-2 mt-4">
                  <div className="border-green-400 text-h2 bg-gradient-to-tr border-2 to-25%  to-black from-green-400 px-2 py-1 text-white rounded-full w-fit">
                    -50% offerts
                  </div>
                  <PrimaryButtonWhite
                    onClick={() => {
                      handleStartCheckoutSession("3_years");
                    }}
                  >
                    Choisir
                  </PrimaryButtonWhite>
                </div>
              </div>{" "}
              <img
                src="https://i.pinimg.com/1200x/78/59/f4/7859f4e95c80233338160901a962475a.jpg"
                alt="Background"
                className="w-full h-full object-cover  rounded-3xl absolute "
              />
            </div>
          </div>
        )}
        {orgData.facturation?.plan === "active" && (
          <div className="flex flex-row items-start gap-4 w-full max-w-c1k mt-4 ">
            <div className="bg-black border-2 relative border-black text-white rounded-3xl flex flex-col h-fit  w-2/5 shadow-xl">
              <div className="flex flex-col p-6 z-10 ">
                <div className="relative flex -top-3 items-center justify-center rounded-full px-2  w-full"></div>
                <div className="text-hx2 font-bold">
                  {orgData.facturation?.engagement === "3_years"
                    ? "Plan 3 ans"
                    : orgData.facturation?.engagement === "annual"
                    ? "Plan 1 an"
                    : orgData.facturation?.engagement === "monthly"
                    ? "Plan 1 mois"
                    : "Plan test"}{" "}
                  en cours
                </div>
                <div className="text-hx2 ">
                  {getPriceFromPlan(orgData.facturation?.engagement)}
                  <span className="text-h2 text-wshadow_80"> /mois HT</span>
                </div>

                <div className="flex flex-row items-end justify-between gap-2 mt-4">
                  <PrimaryButtonWhite
                    onClick={() => {
                      handleOpenStripePortal();
                    }}
                  >
                    Modifier
                  </PrimaryButtonWhite>
                </div>
              </div>
              <img
                src="https://i.pinimg.com/1200x/78/59/f4/7859f4e95c80233338160901a962475a.jpg"
                alt="Background"
                className="w-full h-full object-cover  rounded-3xl absolute "
              />
            </div>
          </div>
        )}{" "}
        {userData.admin && (
          <div className="bg-white mt-6 text-shadow_25 rounded-3xl flex flex-col h-fit w-fit ">
            <div className="flex flex-row items-center justify-end gap-2 mt-4">
              <SecondaryButton
                onClick={() => {
                  handleStartCheckoutSession("test");
                }}
              >
                Choisir test
              </SecondaryButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Facturation;

export const getPriceFromPlan = (engagement: string): string => {
  if (engagement === "test") {
    return "0€";
  }
  if (engagement === "monthly") {
    return "45€";
  } else if (engagement === "annual") {
    return "36€";
  } else if (engagement === "3_years") {
    return "23€";
  }

  return "0€"; // Default case
};
